/*
  Datums-Helfer der Zeitraum-Navigation (AppPeriodNavigator, AppPeriodPicker).

  Reines JavaScript ohne Abhängigkeiten – hier steht die eine Definition davon,
  wie ein Zeitraum heisst ("KW 32 · 03 - 09. Aug. 2026", "August 2026") und wie
  Wochen begrenzt werden (Montag bis Sonntag, ISO-Wochennummern). Die Seiten,
  die die Navigation benutzen, leiten daraus ihre Beschriftungen ab, statt
  eigene zu bilden.
*/

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_SHORT_FORMATTER = new Intl.DateTimeFormat("de-CH", { month: "short" });
const MONTH_LONG_FORMATTER = new Intl.DateTimeFormat("de-CH", { month: "long" });

export function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  const isoDateMatch = raw.match(ISO_DATE_PATTERN);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toIsoDate(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function capitalizeLabel(value) {
  const label = String(value || "");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatDayMonthLabel(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

/* Die Woche beginnt am Montag. */
export function getWeekBounds(value) {
  const base = parseDateValue(value) || new Date();
  const weekday = base.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() + mondayOffset);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { start, end };
}

export function isSameMonth(left, right) {
  const leftDate = parseDateValue(left);
  const rightDate = parseDateValue(right);
  if (!leftDate || !rightDate) return false;
  return leftDate.getFullYear() === rightDate.getFullYear() && leftDate.getMonth() === rightDate.getMonth();
}

export function isSameWeek(left, right) {
  const leftWeek = getWeekBounds(left);
  const rightWeek = getWeekBounds(right);
  return toIsoDate(leftWeek.start) === toIsoDate(rightWeek.start);
}

export function getIsoWeekNumber(value) {
  const base = parseDateValue(value) || new Date();
  const date = new Date(Date.UTC(base.getFullYear(), base.getMonth(), base.getDate()));
  const weekday = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86_400_000 + 1) / 7);
}

export function formatMonthTitle(value) {
  const base = parseDateValue(value) || new Date();
  const label = new Intl.DateTimeFormat("de-CH", { month: "long", year: "numeric" }).format(base);

  return capitalizeLabel(label);
}

/*
  Monat und Jahr stehen nur dort, wo sie etwas unterscheiden: liegt die Woche in
  einem Monat, trägt nur das Ende den Monatsnamen.
*/
export function formatWeekTitle(value) {
  const { start, end } = getWeekBounds(value);
  const weekNumber = getIsoWeekNumber(start);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  const startFormatter = new Intl.DateTimeFormat("de-CH", {
    day: "2-digit",
    month: sameMonth ? undefined : "short",
    year: sameYear ? undefined : "numeric",
  });
  const endFormatter = new Intl.DateTimeFormat("de-CH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `KW ${weekNumber} · ${startFormatter.format(start)} - ${endFormatter.format(end)}`;
}

export function formatWeekRange(value) {
  const { start, end } = getWeekBounds(value);
  return `${formatDayMonthLabel(start)} - ${formatDayMonthLabel(end)}`;
}

export function formatPeriodTitle(viewMode, value) {
  return viewMode === "month" ? formatMonthTitle(value) : formatWeekTitle(value);
}

export function getMonthPickerOptions(year) {
  const normalizedYear = Number(year) || new Date().getFullYear();
  return Array.from({ length: 12 }, (_item, monthIndex) => {
    const date = new Date(normalizedYear, monthIndex, 1);
    return {
      key: `${normalizedYear}-${String(monthIndex + 1).padStart(2, "0")}`,
      date,
      monthIndex,
      label: capitalizeLabel(MONTH_LONG_FORMATTER.format(date)),
      shortLabel: capitalizeLabel(MONTH_SHORT_FORMATTER.format(date).replace(".", "")),
    };
  });
}

/* Alle Wochen, die den Monat berühren – auch die, die in ihn hineinragen. */
export function getWeekPickerOptions(year, monthIndex) {
  const normalizedYear = Number(year) || new Date().getFullYear();
  const normalizedMonth = Math.max(0, Math.min(11, Number(monthIndex) || 0));
  const monthStart = new Date(normalizedYear, normalizedMonth, 1);
  const monthEnd = new Date(normalizedYear, normalizedMonth + 1, 0);
  const firstWeekStart = getWeekBounds(monthStart).start;
  const options = [];

  for (
    let cursor = new Date(firstWeekStart.getFullYear(), firstWeekStart.getMonth(), firstWeekStart.getDate());
    cursor <= monthEnd;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7)
  ) {
    const bounds = getWeekBounds(cursor);
    const intersectsMonth = bounds.end >= monthStart && bounds.start <= monthEnd;
    if (!intersectsMonth) continue;

    options.push({
      key: toIsoDate(bounds.start),
      date: bounds.start,
      weekNumber: getIsoWeekNumber(bounds.start),
      label: `KW ${getIsoWeekNumber(bounds.start)}`,
      rangeLabel: formatWeekRange(bounds.start),
    });
  }

  return options;
}
